import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const MAX_SCALE = 4;
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

// Full-screen photo viewer: pinch / double-tap zoom, drag to pan, swipe down to close.
// The phone's back button closes it (a history entry is pushed while open).
export default function PhotoViewer({ src, alt = '', onClose }) {
  const [t, setT] = useState({ scale: 1, x: 0, y: 0 });
  const [dragY, setDragY] = useState(0);
  const pointers = useRef(new Map());
  const start = useRef(null);
  const lastTap = useRef(0);
  const closedByPop = useRef(false);

  const close = useCallback(() => {
    if (closedByPop.current) { onClose(); return; }
    window.history.back();
  }, [onClose]);

  useEffect(() => {
    window.history.pushState({ photoViewer: true }, '');
    const onPop = () => { closedByPop.current = true; onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, close]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    start.current = pts.length === 2
      ? { mode: 'pinch', d: dist(pts[0], pts[1]), scale: t.scale }
      : { mode: 'drag', x: e.clientX, y: e.clientY, tx: t.x, ty: t.y };
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId) || !start.current) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    const s = start.current;
    if (s.mode === 'pinch' && pts.length === 2) {
      const scale = Math.min(MAX_SCALE, Math.max(1, s.scale * (dist(pts[0], pts[1]) / s.d)));
      setT(p => (scale === 1 ? { scale: 1, x: 0, y: 0 } : { ...p, scale }));
    } else if (s.mode === 'drag') {
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (t.scale > 1) setT(p => ({ ...p, x: s.tx + dx, y: s.ty + dy }));
      else setDragY(Math.max(0, dy));
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (start.current?.mode === 'drag' && t.scale === 1) {
      if (dragY > 120) { close(); return; }
      setDragY(0);
      const now = Date.now();
      if (now - lastTap.current < 300) setT({ scale: 2, x: 0, y: 0 });
      lastTap.current = now;
    } else if (start.current?.mode === 'drag' && t.scale > 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) setT({ scale: 1, x: 0, y: 0 });
      lastTap.current = now;
    }
    start.current = pointers.current.size ? start.current : null;
  };

  return createPortal(
    <div data-testid="photo-viewer" role="dialog" aria-label="Photo viewer" onClick={(e) => e.stopPropagation()}
      style={{ position: 'fixed', inset: 0, zIndex: 2100, background: `rgba(0,0,0,${dragY ? Math.max(0.4, 0.95 - dragY / 400) : 0.95})`, display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none', overflow: 'hidden' }}>
      <img src={src} alt={alt} draggable={false}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', userSelect: 'none', touchAction: 'none',
          transform: `translate(${t.x}px, ${t.y + dragY}px) scale(${t.scale})`, transition: start.current ? 'none' : 'transform 0.2s ease' }} />
      <button type="button" onClick={(e) => { e.stopPropagation(); close(); }} aria-label="Close photo" data-testid="photo-viewer-close"
        style={{ position: 'absolute', top: 'calc(12px + env(safe-area-inset-top, 0px))', right: 12, width: 44, height: 44, borderRadius: 22, border: 'none', background: 'rgba(255,255,255,0.18)', color: '#FFF', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <X size={22} />
      </button>
    </div>,
    document.body
  );
}
